import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Save, ArrowLeft, Plus, ChevronRight, List, Search, Shield, Zap, Info, Tag, Paperclip, History, AlertTriangle, Trash2, FlaskConical } from 'lucide-react';
import { swalTheme, swalDangerColor } from '../lib/swalTheme';

// المودالات اللي إنت بتستخدمها في الـ PO/PR
import UniversalLookup from './UniversalLookup';
import StatusModal from './StatusModal';
import StatusHistoryModal from './StatusHistoryModal';
import AttachmentsModal from './AttachmentsModal';
import AuditHistoryModal from './AuditHistoryModal';
import SmartPrintButton from './PrintButton';

const SafetyPlanDetails = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [activeTab, setActiveTab] = useState('Plan');
  const [selectedHazardIdx, setSelectedHazardIdx] = useState(null);
  const [selectedLotoIdx, setSelectedLotoIdx] = useState(null);
  const [selectedHazmatIdx, setSelectedHazmatIdx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [count, setCount] = useState(0);

  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const [data, setData] = useState({
    safetypnum: '', description: '', is_active: true, status: 'DRAFT',
    siteid: session.site_id || '', orgid: session.org_id || '',
    hazards: [],
    tagouts: [],
    hazmat: [],
    work_assets: []
  });

  const [lookup, setLookup] = useState({ isOpen: false, title: '', type: '', field: '', filters: {}, targetIdx: null, subIdx: null });

  // 1. جلب البيانات الأساسية مع تأمين المصفوفات
  useEffect(() => {
    if (!isNew) {
      axios.get(`/api/safety-plans/${id}`).then(res => {
        const resData = res.data;
        resData.hazards = resData.hazards || [];
        resData.tagouts = resData.tagouts || [];
        resData.hazmat = resData.hazmat || [];
        resData.work_assets = resData.work_assets || [];
        setData(resData);
      });
    }
  }, [id, isNew]);

  // 2. جلب عدد المرفقات
  const fetchCount = useCallback(async () => {
    if (!data.safetypnum || isNew) { setCount(0); return; }
    try {
      const res = await axios.get(`/api/attachments/count/SAFETYPLAN/${data.safetypnum}`);
      setCount(res.data.count || 0);
    } catch (err) { console.error(err); }
  }, [data.safetypnum, isNew]);

  useEffect(() => { fetchCount(); }, [fetchCount]);

  useEffect(() => {
    const openLog = () => setShowAudit(true);
    window.addEventListener('GLOBAL_LOG_HANDLER', openLog);
    return () => window.removeEventListener('GLOBAL_LOG_HANDLER', openLog);
  }, []);

  // --- 🚀 دوال الإضافة (Add Functions) ---
    const addHazard = () => {
        const newHazard = { hazardnum: '', description: '', priority: 'MEDIUM', precautions: [] };
        setData({ ...data, hazards: [...data.hazards, newHazard] });
        setSelectedHazardIdx(data.hazards.length);
    };

    const addPrecaution = (hIdx) => {
        const newHazards = [...data.hazards];
        newHazards[hIdx].precautions = newHazards[hIdx].precautions || [];
        newHazards[hIdx].precautions.push({ precautionnum: '', description: '' });
        setData({ ...data, hazards: newHazards });
    };

    const addLotoStep = () => {
        const newStep = { tagoutnum: '', sequence: data.tagouts.length + 1, description: '', required_state: '' };
        setData({ ...data, tagouts: [...data.tagouts, newStep] });
        setSelectedLotoIdx(data.tagouts.length);
    };

    const addHazmatRow = () => {
        const newMat = { itemnum: '', description: '', health_rating: '' };
        setData({ ...data, hazmat: [...data.hazmat, newMat] });
        setSelectedHazmatIdx(data.hazmat.length);
    };
    // 1. دالة إضافة سطر جديد للأصول (أو نفتح اللوك اب مباشرة)
    const addWorkAsset = () => {
        // بنفتح اللوك اب ونعرفه إننا عاوزين نربط في جدول الـ work_assets
        setLookup({
            isOpen: true,
            title: t('safety.lookup.selectAssetOrLocation', 'Select Asset or Location'),
            type: 'assets', // ممكن تخليها assets_locations لو عامل كويري مشترك
            field: 'work_asset_row',
            targetIdx: data.work_assets?.length || 0
        });
    };


    // --- 🗑️ دوال الحذف المؤمنة (Delete Functions) ---
    const deleteHazard = (idx) => {
        Swal.fire({
            ...swalTheme(),
            title: t('common.are_you_sure', 'Are you sure?'),
            text: t('safety.details.deleteHazardConfirm', 'This will remove the hazard and all its precautions!'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: swalDangerColor(),
            confirmButtonText: t('common.yes_delete', 'Yes, delete it!')
        }).then((result) => {
            if (result.isConfirmed) {
                const newHazards = data.hazards.filter((_, i) => i !== idx);
                setData({ ...data, hazards: newHazards });
                setSelectedHazardIdx(null);
                toast.success(t('safety.details.hazardRemoved', 'Hazard removed'));
            }
        });
    };

    const deletePrecaution = (hIdx, pIdx) => {
        const newHazards = [...data.hazards];
        newHazards[hIdx].precautions = newHazards[hIdx].precautions.filter((_, i) => i !== pIdx);
        setData({ ...data, hazards: newHazards });
        toast.success(t('safety.details.precautionRemoved', 'Precaution removed'));
    };

    const deleteLotoStep = (idx) => {
        Swal.fire({
            ...swalTheme(),
            title: t('safety.details.deleteStepTitle', 'Delete Step?'),
            text: t('safety.details.deleteStepConfirm', 'Do you want to remove this isolation step?'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('common.delete', 'Delete')
        }).then((result) => {
            if (result.isConfirmed) {
                const newLoto = data.tagouts.filter((_, i) => i !== idx);
                const reorderedLoto = newLoto.map((item, i) => ({ ...item, sequence: i + 1 }));
                setData({ ...data, tagouts: reorderedLoto });
                setSelectedLotoIdx(null);
                toast.success(t('safety.details.lotoStepRemoved', 'LOTO step removed'));
            }
        });
    };

    const deleteHazmat = (idx) => {
        const newHazmat = data.hazmat.filter((_, i) => i !== idx);
        setData({ ...data, hazmat: newHazmat });
        setSelectedHazmatIdx(null);
        toast.success(t('safety.details.materialRemoved', 'Material removed'));
    };
    // 2. دالة حذف أصل مرتبط
    const deleteWorkAsset = (idx) => {
        Swal.fire({
            ...swalTheme(),
            title: t('safety.details.removeAssetTitle', 'Remove Asset?'),
            text: t('safety.details.removeAssetConfirm', 'This asset will no longer be linked to this safety plan.'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: swalDangerColor(),
            confirmButtonText: t('safety.details.yesRemove', 'Yes, remove it')
        }).then((result) => {
            if (result.isConfirmed) {
                const newAssets = data.work_assets.filter((_, i) => i !== idx);
                setData({ ...data, work_assets: newAssets });
                toast.success(t('safety.details.assetUnlinked', 'Asset unlinked'));
            }
        });
    };
  const handleSave = async () => {
    try {
      setLoading(true);
      const res = await axios.post('/api/safety-plans/save', data);
      toast.success(t('safety.details.saveSuccess', 'Safety Plan Saved!'));
      if (isNew) navigate(`/safety/${res.data.safetypnum}`);
    } catch (err) { toast.error(t('common.save_fail', 'Save failed')); }
    finally { setLoading(false); }
  };

    const handleDelete = () => {
        Swal.fire({
            ...swalTheme(),
            title: t('common.are_you_sure', 'Are you sure?'), text: t('safety.details.deletePlanConfirm', 'This will delete the safety plan permanently!'),
            icon: 'warning', showCancelButton: true, confirmButtonColor: swalDangerColor(),
            confirmButtonText: t('common.yes_delete', 'Yes, delete it!')
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`/api/safety-plans/${data.safetypnum}`);
                    toast.success(t('safety.details.deleteSuccess', 'Safety Plan deleted!'));
                    navigate('/safety');
                } catch (err) { toast.error(t('common.delete_fail', 'Delete failed')); }
            }
        });
    };



  const handleLookupSelect = async (row) => {
    const newData = { ...data };
    const { field, targetIdx, subIdx } = lookup;

    if (field === 'hazardnum') {
      newData.hazards[targetIdx].hazardnum = row.hazardnum;
      newData.hazards[targetIdx].hazardtype = row.hazardtype;
      newData.hazards[targetIdx].description = row.description;
      try {
        const res = await axios.get(`/api/hazards/${row.hazardnum}/precautions`);
        if (res.data && res.data.length > 0) {
          // بنضيف الاحتياطات اللي جت من الماستر للسطر ده بالذات
          newData.hazards[targetIdx].precautions = res.data;
          toast.success(t('safety.details.autoFillPrecautions', 'Auto-filled {{count}} precautions for this hazard', { count: res.data.length }));
        }
      } catch (err) {
        console.error("Auto-fill failed", err);
      }
    } else if (field === 'precautionnum') {
      newData.hazards[targetIdx].precautions[subIdx].precautionnum = row.precautionnum;
      newData.hazards[targetIdx].precautions[subIdx].description = row.description;
    } else if (field === 'tagoutnum') {
      newData.tagouts[targetIdx].tagoutnum = row.tagoutnum;
      newData.tagouts[targetIdx].description = row.description;
      newData.tagouts[targetIdx].required_state = row.required_state;
    } else if (field === 'hazmat_item') {
      newData.hazmat[targetIdx].itemnum = row.itemnum || row.value;
      newData.hazmat[targetIdx].description = row.description;
    } else if (field === 'work_asset_row') {
      // بنجهز السجل الجديد
      const newAssetLink = {
        assetnum: row.assetnum || null,
        location: row.location || null,
        description: row.description || row.asset_desc || '',
        siteid: row.siteid || data.siteid || '',
        orgid: row.orgid || data.orgid || ''
      };

      // لو المصفوفة مش موجودة بنعرفها
      if (!newData.work_assets) newData.work_assets = [];

      // بنضيف السجل في المصفوفة
      newData.work_assets.push(newAssetLink);
      toast.success(t('safety.details.assetLinked', 'Asset linked to plan'));
    } else {
      newData[field] = row.value || row.site_id || row.org_id || row.assetnum || row.location_id;
    }
    setData(newData);
    setLookup({ ...lookup, isOpen: false });
  };
    // --- Global Handlers for Save/Delete (تقدر تشغلهم من أي مكان في التطبيق) ---
    useEffect(() => {
        const triggerSave = () => handleSave();
        window.addEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
        return () => window.removeEventListener('GLOBAL_SAVE_HANDLER', triggerSave);
    }, [handleSave]);

    useEffect(() => {
        const triggerDelete = () => handleDelete();
        window.addEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
        return () => window.removeEventListener('GLOBAL_DELETE_HANDLER', triggerDelete);
    }, [handleDelete]);

  return (
    <div className="flex flex-col h-screen bg-surface-app text-[11px] font-sans uppercase">
      <UniversalLookup {...lookup} onClose={() => setLookup({...lookup, isOpen: false})} onSelect={handleLookupSelect} />

      {showStatusModal && <StatusModal currentStatus={data.status} recordId={data.safetypnum} appName="SAFETY" onClose={() => setShowStatusModal(false)} />}
      {showHistoryModal && <StatusHistoryModal isOpen={showHistoryModal} appName="SAFETY" recordId={data.safetypnum} onClose={() => setShowHistoryModal(false)} />}
      {!isNew && <AttachmentsModal isOpen={isDocModalOpen} onClose={() => {setIsDocModalOpen(false); fetchCount();}} ownertable="SAFETYPLAN" ownerid={data.safetypnum} />}
      <AuditHistoryModal isOpen={showAudit} onClose={() => setShowAudit(false)} tableName="safety_plans" recordId={data.safetypnum} />

      {/* Header Bar - PO Style */}
      <div className="bg-surface-card p-2 border-b flex justify-between items-center shadow-sm z-20 font-bold tracking-tighter uppercase">
        <div className="flex gap-6 ml-4">
          <button onClick={() => navigate('/safety')} className="p-1 hover:bg-surface-panel rounded-full text-primary-dark"><ArrowLeft size={18} /></button>
          <HeaderField label={t('safety.fields.planNum', 'Plan Num')} value={data.safetypnum} isBold width="w-32" disabled={!isNew} onChange={v => setData({...data, safetypnum: v})} />
          <HeaderField label={t('safety.fields.description', 'Description')} value={data.description} onChange={v => setData({...data, description: v})} width="w-[300px]" />
          <HeaderField label={t('assets.headers.status', 'Status')} value={data.status} disabled width="w-24" />
          <HeaderField label={t('common.site', 'Site')} value={data.siteid} disabled width="w-24" />
        </div>
        <div className="flex gap-2 mr-4">
            <button onClick={() => setShowStatusModal(true)} className="p-2 text-primary-dark hover:bg-primary/10 rounded"><Tag size={16}/></button>
            {!isNew && <SmartPrintButton moduleName="SAFETYPLAN" refId={data.safetypnum} />}
            {!isNew && (
              <button onClick={() => setIsDocModalOpen(true)} className="relative p-2 text-primary-dark rounded-sm hover:bg-primary hover:text-white transition-all">
                <Paperclip size={18} />
                {count > 0 && <span className="absolute -top-1 -right-1 bg-status-danger text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center border border-white">{count}</span>}
              </button>
            )}
            <button onClick={() => setShowHistoryModal(true)} className="p-2 text-primary-dark hover:bg-primary/10 rounded"><History size={16}/></button>
            <button onClick={handleSave} disabled={loading} className="bg-primary text-white px-8 py-2 rounded font-black flex items-center gap-2 hover:bg-primary-dark shadow-lg transition-all">
               {loading ? t('common.saving', 'Saving...') : <><Save size={14} /> {t('common.save', 'SAVE')}</>}
            </button>
        </div>
      </div>

      {/* Tabs Menu - Blue Style */}
      <div className="flex bg-primary-dark text-white shadow-md">
        {[
          { id: 'Plan', label: t('safety.details.tabPlan', 'Safety Plan'), icon: <Info size={14}/> },
          { id: 'Hazards', label: t('safety.details.tabHazards', 'Hazards & Precautions'), icon: <Shield size={14}/> },
          { id: 'Hazmat', label: t('safety.details.tabHazmat', 'Hazardous Materials'), icon: <FlaskConical size={14}/> },
          { id: 'LOTO', label: t('safety.details.tabLoto', 'Lock Out / Tag Out'), icon: <Zap size={14}/> },
          { id: 'Assets', label: t('safety.details.tabAssets', 'Work Assets'), icon: <List size={14} /> }
        ].map(tb => (
          <button key={tb.id} onClick={() => { setActiveTab(tb.id); setSelectedHazardIdx(null); setSelectedLotoIdx(null); setSelectedHazmatIdx(null); }} className={`px-10 py-3 flex items-center gap-2 font-black border-r border-white/5 transition-all ${activeTab === tb.id ? 'bg-surface-card text-primary-dark' : 'opacity-75 hover:opacity-100'}`}>
            {tb.icon} {tb.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 bg-surface-panel">
        {/* --- Tab 1: General Plan --- */}
        {activeTab === 'Plan' && (
          <div className="grid grid-cols-12 gap-6 max-w-7xl mx-auto animate-in fade-in duration-500">
            <div className="col-span-8 space-y-6">
              <SectionCard title={t('safety.details.planInformation', 'Plan Information')}>
                <div className="grid grid-cols-2 gap-8">
                  <EditableField label={t('safety.fields.planNumber', 'Plan Number')} value={data.safetypnum} disabled={!isNew} />
                  <EditableField label={t('common.org', 'Organization')} value={data.orgid} disabled />
                  <div className="col-span-2">
                    <EditableField label={t('safety.fields.detailedDescription', 'Detailed Description')} value={data.description} onChange={v => setData({...data, description: v})} />
                  </div>
                </div>
              </SectionCard>
            </div>
          </div>
        )}

        {/* --- Tab 2: Hazards & Precautions --- */}
        {activeTab === 'Hazards' && (
        <div className="max-w-7xl mx-auto space-y-6 animate-in slide-in-from-bottom duration-500">
            <SectionCard title={t('safety.details.linkedHazards', 'Linked Hazards')}>
            <div className="bg-surface-card border rounded shadow-sm overflow-hidden">
                <table className="w-full text-left">
                <thead className="bg-surface-panel border-b text-ink-secondary font-bold uppercase text-[9px]">
                    <tr>
                    <th className="p-2 w-8"></th>
                    <th className="p-2 border-r">{t('hazards.fields.hazardNum', 'Hazard Num')}</th>
                    <th className="p-2 border-r">{t('safety.details.hazardDescription', 'Hazard Description')}</th>
                    <th className="p-2 border-r text-center w-32">{t('hazards.fields.priority', 'Priority')}</th>
                    <th className="p-2 text-center w-12">{t('common.action', 'Action')}</th>
                    </tr>
                </thead>
                <tbody>
                    {data.hazards.map((h, i) => (
                    <tr key={i} onClick={() => setSelectedHazardIdx(i)}
                        className={`border-b hover:bg-primary/5 cursor-pointer transition-colors ${selectedHazardIdx === i ? 'bg-primary/10 border-l-4 border-l-primary-dark' : ''}`}>
                        <td className="p-2 text-primary">{selectedHazardIdx === i && <ChevronRight size={14} className="animate-pulse"/>}</td>
                        <td className="p-2 border-r font-black text-primary-dark uppercase">{h.hazardnum || t('safety.details.newHazard', 'NEW_HAZARD')}</td>
                        <td className="p-2 border-r font-semibold text-ink">{h.description}</td>
                        <td className="p-2 border-r text-center uppercase font-black text-status-warning">{h.priority || 'MEDIUM'}</td>
                        <td className="p-2 text-center text-status-danger hover:bg-status-danger-bg cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); deleteHazard(i); }}>
                            <Trash2 size={12}/>
                        </td>
                    </tr>
                    ))}
                </tbody>
                </table>
                <button onClick={addHazard} className="m-2 bg-surface-panel text-ink hover:bg-primary-dark hover:text-white px-4 py-1 rounded-sm font-black flex items-center gap-2 transition-all">
                <Plus size={12}/> {t('safety.details.newHazardLine', 'NEW HAZARD LINE')}
                </button>
            </div>
            </SectionCard>

            {selectedHazardIdx !== null && data.hazards[selectedHazardIdx] && (
            <div className="grid grid-cols-12 gap-6 animate-in zoom-in-95 duration-300">
                <div className="col-span-4">
                <SectionCard title={`${t('safety.details.hazardDetails', 'Hazard Details')}: ${data.hazards[selectedHazardIdx].hazardnum || ''}`}>
                    <div className="space-y-4">
                    <EditableField label={t('hazards.fields.hazardNum', 'Hazard Num')} value={data.hazards[selectedHazardIdx].hazardnum}
                        onSearch={() => setLookup({isOpen: true, title: t('safety.lookup.selectHazardMaster', 'Select Hazard Master'), type: 'hazards', field: 'hazardnum', targetIdx: selectedHazardIdx})} />
                    <EditableField label={t('hazards.fields.hazardType', 'Hazard Type')} value={data.hazards[selectedHazardIdx].hazardtype} disabled />
                    <div className="pt-2">
                        <label className="text-ink-secondary font-black text-[8px] uppercase tracking-widest italic">{t('safety.details.hazardStatement', 'Hazard Statement')}</label>
                        <textarea className="w-full mt-1 p-2 bg-surface-panel border rounded outline-none font-bold text-primary-dark text-[10px]" rows="3"
                        value={data.hazards[selectedHazardIdx].description} onChange={(e) => {
                            let newH = [...data.hazards]; newH[selectedHazardIdx].description = e.target.value; setData({...data, hazards: newH});
                        }} />
                    </div>
                    </div>
                </SectionCard>
                </div>

                <div className="col-span-8">
                <SectionCard title={`${t('safety.details.precautionsFor', 'Precautions for')} ${data.hazards[selectedHazardIdx].hazardnum || t('safety.details.thisHazard', 'this hazard')}`}>
                    <div className="bg-surface-card border rounded shadow-sm overflow-hidden text-[10px]">
                    <table className="w-full text-left">
                        <thead className="bg-surface-panel border-b text-ink-secondary font-black uppercase text-[8px]">
                        <tr>
                            <th className="p-2 border-r w-32">{t('precautions.fields.precautionNum', 'Precaution Num')}</th>
                            <th className="p-2 border-r">{t('hazards.details.instructionDesc', 'Instruction / Description')}</th>
                            <th className="p-2 w-12 text-center">{t('common.delete', 'Delete')}</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(data.hazards[selectedHazardIdx].precautions || []).map((p, pi) => (
                            <tr key={pi} className="border-b">
                            <td className="p-2 border-r font-black text-primary-dark uppercase group">
                                <div className="flex justify-between items-center">
                                    {p.precautionnum || '---'}
                                    <Search size={10} className="text-primary cursor-pointer"
                                    onClick={() => setLookup({isOpen: true, title: t('hazards.lookup.selectPrecaution', 'Select Precaution'), type: 'precautions', field: 'precautionnum', targetIdx: selectedHazardIdx, subIdx: pi})}/>
                                </div>
                            </td>
                            <td className="p-2 border-r font-medium text-ink-secondary">{p.description}</td>
                            <td className="p-2 text-center text-status-danger opacity-60 hover:opacity-100 cursor-pointer"
                                onClick={() => deletePrecaution(selectedHazardIdx, pi)}>
                                <Trash2 size={10}/>
                            </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    <button onClick={() => addPrecaution(selectedHazardIdx)} className="m-2 text-primary-dark font-black text-[9px] flex items-center gap-1 hover:underline">
                        <Plus size={10}/> {t('safety.details.addPrecaution', 'ADD PRECAUTION')}
                    </button>
                    </div>
                </SectionCard>
                </div>
            </div>
            )}
        </div>
        )}

        {/* --- Tab 3: Hazardous Materials --- */}
        {activeTab === 'Hazmat' && (
            <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom duration-500 font-bold uppercase">
                <SectionCard title={t('safety.details.hazmatTitle', 'Hazardous Materials (Hazmat)')}>
                    <div className="bg-surface-card border rounded overflow-hidden">
                        <table className="w-full text-left text-[10px]">
                            <thead className="bg-surface-panel border-b text-ink-secondary font-bold uppercase text-[9px]">
                                <tr>
                                    <th className="p-2 w-8"></th>
                                    <th className="p-2 border-r">{t('safety.fields.itemNum', 'Item Num')}</th>
                                    <th className="p-2 border-r">{t('safety.fields.materialDescription', 'Material Description')}</th>
                                    <th className="p-2 border-r text-center w-24">{t('safety.fields.healthRating', 'Health Rating')}</th>
                                    <th className="p-2 text-center w-12">{t('common.delete', 'Delete')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.hazmat.map((m, i) => (
                                    <tr key={i} onClick={() => setSelectedHazmatIdx(i)} className={`border-b hover:bg-primary/5 cursor-pointer ${selectedHazmatIdx === i ? 'bg-primary/10 border-l-4 border-l-primary-dark' : ''}`}>
                                        <td className="p-2 text-primary">{selectedHazmatIdx === i && <ChevronRight size={14} />}</td>
                                        <td className="p-2 border-r text-primary-dark font-black">{m.itemnum || t('safety.details.new', 'NEW')}</td>
                                        <td className="p-2 border-r font-semibold text-ink">{m.description}</td>
                                        <td className="p-2 border-r text-center text-status-danger font-black">{m.health_rating || '3'}</td>
                                        <td className="p-2 text-center" onClick={(e) => { e.stopPropagation(); deleteHazmat(i); }}>
                                            <Trash2 size={12} className="text-status-danger opacity-70 hover:opacity-100" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <button onClick={addHazmatRow} className="m-2 bg-surface-panel text-ink hover:bg-primary-dark hover:text-white px-4 py-1 rounded-sm font-black flex items-center gap-2">
                            <Plus size={12} /> {t('safety.details.addHazmatLine', 'ADD HAZMAT LINE')}
                        </button>
                    </div>
                </SectionCard>
                {selectedHazmatIdx !== null && data.hazmat[selectedHazmatIdx] && (
                    <div className="bg-surface-card border p-6 grid grid-cols-4 gap-8 shadow-md rounded-sm border-t-4 border-t-primary-dark animate-in zoom-in-95">
                        <EditableField label={t('safety.fields.materialItemNum', 'Material (Item Num)')} value={data.hazmat[selectedHazmatIdx].itemnum} onSearch={() => setLookup({ isOpen: true, title: t('safety.lookup.hazmatLookup', 'Hazmat Lookup'), type: 'items', field: 'hazmat_item', targetIdx: selectedHazmatIdx })} />
                        <div className="col-span-2">
                            <EditableField label={t('safety.fields.healthRatingPrecautions', 'Health Rating / Precautions')} value={data.hazmat[selectedHazmatIdx].health_rating} onChange={v => {
                                const nm = [...data.hazmat]; nm[selectedHazmatIdx].health_rating = v; setData({ ...data, hazmat: nm });
                            }} />
                        </div>
                    </div>
                )}
            </div>
        )}

        {/* --- Tab 4: Lock Out / Tag Out (LOTO) --- */}
        {activeTab === 'LOTO' && (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom duration-500">
             <div className="bg-surface-card border rounded shadow-sm overflow-hidden text-[10px]">
                <table className="w-full text-left">
                  <thead className="bg-surface-panel border-b text-ink-secondary font-bold uppercase text-[9px]">
                    <tr>
                      <th className="p-2 w-8"></th>
                      <th className="p-2 border-r">{t('safety.fields.sequence', 'Sequence')}</th>
                      <th className="p-2 border-r">{t('tagouts.fields.tagOutNum', 'Tag Out Num')}</th>
                      <th className="p-2 border-r">{t('tagouts.fields.description', 'Description')}</th>
                      <th className="p-2">{t('tagouts.fields.requiredState', 'Req. State')}</th>
                      <th className="p-2 w-12 text-center">{t('common.action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tagouts.map((tg, i) => (
                      <tr key={i} onClick={() => setSelectedLotoIdx(i)} className={`border-b hover:bg-primary/5 cursor-pointer ${selectedLotoIdx === i ? 'bg-primary/10 border-l-4 border-l-primary-dark' : ''}`}>
                         <td className="p-2 text-primary">{selectedLotoIdx === i && <ChevronRight size={12}/>}</td>
                         <td className="p-2 border-r font-bold text-center">{tg.sequence}</td>
                         <td className="p-2 border-r font-black text-primary-dark">{tg.tagoutnum || t('safety.details.new', 'NEW')}</td>
                         <td className="p-2 border-r font-semibold">{tg.description}</td>
                         <td className="p-2 font-black text-status-danger">{tg.required_state}</td>
                         <td className="p-2 text-center text-status-danger hover:bg-status-danger-bg cursor-pointer"
                            onClick={(e) => { e.stopPropagation(); deleteLotoStep(i); }}>
                            <Trash2 size={12}/>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
             </div>
             {selectedLotoIdx !== null && data.tagouts[selectedLotoIdx] && (
               <div className="bg-surface-card border p-6 grid grid-cols-4 gap-8 shadow-md rounded-sm border-t-4 border-t-primary-dark animate-in zoom-in-95">
                  <EditableField label={t('tagouts.fields.tagOutNum', 'Tag Out Num')} value={data.tagouts[selectedLotoIdx].tagoutnum} onSearch={() => setLookup({isOpen: true, title: t('hazards.lookup.selectTagOut', 'Select TagOut'), type: 'tagouts', field: 'tagoutnum', targetIdx: selectedLotoIdx})} />
                  <EditableField label={t('safety.fields.sequence', 'Sequence')} type="number" value={data.tagouts[selectedLotoIdx].sequence} disabled />
                  <div className="col-span-2">
                    <EditableField label={t('safety.fields.lotoDescription', 'LOTO Description')} value={data.tagouts[selectedLotoIdx].description} onChange={v => {
                        const nl = [...data.tagouts]; nl[selectedLotoIdx].description = v; setData({...data, tagouts: nl});
                    }} />
                  </div>
               </div>
             )}
             <button onClick={addLotoStep} className="bg-primary text-white px-8 py-1.5 rounded-sm font-black flex items-center gap-2">
                <Plus size={14}/> {t('safety.details.addLotoStep', 'Add LOTO Step')}
             </button>
          </div>
        )}
        {activeTab === 'Assets' && (
          <div className="max-w-7xl mx-auto space-y-4 animate-in slide-in-from-bottom duration-500 font-bold uppercase">
            <SectionCard title={t('safety.details.workAssetsTitle', 'Work Assets (Assets & Locations Covered)')}>
              <div className="bg-surface-card border rounded overflow-hidden">
                <table className="w-full text-left">
                  <thead className="bg-surface-panel border-b text-ink-secondary font-bold uppercase text-[9px]">
                    <tr>
                      <th className="p-2 border-r w-40">{t('safety.fields.assetOrLocation', 'Asset / Location')}</th>
                      <th className="p-2 border-r">{t('safety.fields.description', 'Description')}</th>
                      <th className="p-2 border-r text-center w-24">{t('common.site', 'Site')}</th>
                      <th className="p-2 border-r text-center w-24">{t('common.org', 'Org')}</th>
                      <th className="p-2 text-center w-12">{t('common.action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.work_assets?.length > 0 ? (
                      data.work_assets.map((wa, i) => (
                        <tr key={i} className="border-b hover:bg-primary/5 cursor-pointer">
                          <td className="p-2 border-r font-black text-primary-dark uppercase">
                            {wa.assetnum || wa.location}
                          </td>
                          <td className="p-2 border-r font-semibold text-ink-secondary">
                            {wa.description}
                          </td>
                          <td className="p-2 border-r text-center">{wa.siteid || '-'}</td>
                          <td className="p-2 border-r text-center">{wa.orgid || '-'}</td>
                          <td className="p-2 text-center" onClick={(e) => { e.stopPropagation(); deleteWorkAsset(i); }}>
                             <Trash2 size={12} className="text-status-danger opacity-70 hover:opacity-100 cursor-pointer mx-auto" />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="p-10 text-center text-ink-secondary italic">
                          {t('safety.details.noAssetsLinked', 'No assets or locations linked. This plan is currently generic.')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                <div className="p-2 bg-surface-panel border-t">
                    <button onClick={addWorkAsset} className="bg-primary text-white px-6 py-1.5 rounded-sm font-black flex items-center gap-2 hover:bg-primary-dark transition-all shadow-sm">
                      <Plus size={14}/> {t('safety.details.addAssetLocation', 'ADD ASSET / LOCATION')}
                    </button>
                </div>
              </div>
            </SectionCard>
          </div>
        )}
      </div>
    </div>
  );
};

// المكونات الفرعية
const HeaderField = ({ label, value, onChange, width, isBold, disabled }) => (
  <div className="flex flex-col border-r px-4 last:border-0">
    <label className="text-[8px] text-ink-secondary font-black uppercase tracking-widest mb-0.5 italic">{label}</label>
    <input className={`${width} border-b border-transparent outline-none py-0.5 font-black uppercase ${isBold ? 'text-primary-dark text-[12px]' : 'text-primary'} bg-transparent focus:border-primary`}
           value={value || ''} disabled={disabled} onChange={e => onChange && onChange(e.target.value)} />
  </div>
);

const SectionCard = ({ title, children }) => (
  <div className="bg-surface-card border rounded shadow-sm overflow-hidden transition-all hover:shadow-md">
    <div className="p-2 bg-surface-panel border-b font-black text-ink-secondary uppercase text-[9px] tracking-widest flex items-center gap-2">
      <div className="w-1.5 h-3 bg-primary-dark rounded-full"></div> {title}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const EditableField = ({ label, value, onChange, onSearch, type = "text", disabled }) => (
  <div className="flex flex-col gap-1 border-b border-line group focus-within:border-primary transition-all py-1">
    <label className="text-ink-secondary font-black text-[8px] uppercase tracking-widest italic">{label}</label>
    <div className="flex items-center justify-between pb-0.5">
      <input type={type} disabled={disabled} className="outline-none font-black text-primary-dark bg-transparent w-full text-[11px]"
        value={value || ''} onChange={e => onChange && onChange(e.target.value)} />
      {onSearch && <Search size={12} className="text-primary cursor-pointer hover:text-primary-dark" onClick={onSearch} />}
    </div>
  </div>
);

export default SafetyPlanDetails;
